import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useHistory } from 'react-router-dom';
import API_BASE_URL from '../config';
import CollapsibleSection from '../components/CollapsibleSection';
import { useTheme, THEMES } from '../context/ThemeContext';
import { Palette, Heart, BarChart3, LayoutGrid, Check } from 'lucide-react';
import './AdminDashboard.css';


interface Wedding {
    _id: string;
    coupleName: string;
    groomName: string;
    brideName: string;
    weddingDate: string;
    venue?: string;
    city?: string;
    bannerImage?: string;
    groomProfilePhoto?: string;
    brideProfilePhoto?: string;
    status: string;
    plan?: string;
    storageLimit?: number;
    storageUsage?: number;
    guestLimit?: number;
}

interface AdminProfile {
    _id: string;
    name: string;
    email: string;
    familySide: 'groom' | 'bride';
    wedding: Wedding;
}

const AdminDashboard: React.FC = () => {
    const { accessToken, user, logout, loading: authLoading } = useAuth();
    const history = useHistory();
    const { currentTheme, setTheme } = useTheme();
    const [profile, setProfile] = useState<AdminProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [ceremonies, setCeremonies] = useState<any[]>([]);
    const [guestStats, setGuestStats] = useState({ approvedCount: 0, pendingCount: 0 });
    const [taskStats, setTaskStats] = useState({ pendingCount: 0, completedCount: 0 });

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch Profile
                const profileRes = await fetch(`${API_BASE_URL}/api/admin/profile`, {
                    headers: { Authorization: `Bearer ${accessToken}` },
                });
                const profileData = await profileRes.json();
                if (profileRes.ok) {
                    setProfile(profileData.admin);
                } else if (profileRes.status === 401 || profileRes.status === 403) {
                    logout();
                    history.push('/login');
                    setLoading(false); // Ensure loading is false before redirect
                    return; // Exit early if unauthorized
                } else {
                    setError(profileData.message || 'Failed to fetch profile');
                    setLoading(false);
                    return; // Exit early if profile fetch failed
                }

                // Fetch ceremonies
                const ceremonyRes = await fetch(`${API_BASE_URL}/api/ceremonies`, {
                    headers: { Authorization: `Bearer ${accessToken}` },
                });
                const ceremonyData = await ceremonyRes.json();
                if (ceremonyRes.ok) {
                    setCeremonies(ceremonyData.ceremonies);
                }

                // Fetch Guest Stats
                const guestRes = await fetch(`${API_BASE_URL}/api/guests/stats`, {
                    headers: { Authorization: `Bearer ${accessToken}` },
                });
                const guestData = await guestRes.json();
                if (guestRes.ok) {
                    setGuestStats(guestData);
                }
                // Fetch Task Stats
                const taskRes = await fetch(`${API_BASE_URL}/api/tasks/stats`, {
                    headers: { Authorization: `Bearer ${accessToken}` },
                });
                const taskData = await taskRes.json();
                if (taskRes.ok) {
                    setTaskStats(taskData);
                }
            } catch (err) {
                setError('Failed to connect to server');
            } finally {
                setLoading(false);
            }
        };

        if (!authLoading) {
            if (accessToken) {
                if (user?.role === 'admin') {
                    fetchData();
                } else if (user?.role === 'superadmin') {
                    history.push('/superadmin/dashboard');
                } else {
                    history.push('/dashboard');
                }
            } else {
                history.push('/login');
            }
        }
    }, [accessToken, history, logout, authLoading, user?.role]);

    if (authLoading || loading) {
        return (
            <div className="admin-dashboard loading">
                <div className="loader">✨ Loading your wedding magic...</div>
            </div>
        );
    }

    if (error || !profile) {
        return (
            <div className="admin-dashboard error">
                <div className="error-card">
                    <h2>Oops!</h2>
                    <p>{error || 'Could not load your wedding details.'}</p>
                    <button onClick={() => history.push('/login')}>Go to Login</button>
                </div>
            </div>
        );
    }

    const { wedding } = profile;
    const weddingDate = new Date(wedding.weddingDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });

    const calculateDaysRemaining = (date: string) => {
        const target = new Date(date);
        target.setHours(0, 0, 0, 0);
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const diff = target.getTime() - now.getTime();
        return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
    };

    const daysRemaining = calculateDaysRemaining(wedding.weddingDate);

    return (
        <div className="admin-dashboard px-6 pb-24 max-w-7xl mx-auto w-full flex flex-col gap-6" style={{ width: '100%' }}>
            
            <div className="admin-welcome" style={{ padding: '24px 24px 0 24px' }}>
                <h2>Welcome, {profile.name}</h2>
                <p>Everything you need for {wedding.coupleName}'s special day is right here.</p>
            </div>

            {/* Collapsible Section: Visual Identity */}
            <CollapsibleSection title="Visual Identity" icon={Palette} defaultOpen={false}>
                <div className="theme-picker-grid">
                    {THEMES.map((theme) => {
                        const isActive = currentTheme.id === theme.id;
                        return (
                            <button
                                key={theme.id}
                                onClick={() => setTheme(theme.id)}
                                className={`theme-picker-card ${isActive ? 'active' : ''}`}
                            >
                                <div className="theme-picker-header">
                                    <span className="theme-picker-name">
                                        <span style={{ marginRight: '6px' }}>{theme.emoji}</span>
                                        {theme.name}
                                    </span>
                                    {isActive && (
                                        <div className="theme-picker-check">
                                            <Check size={12} />
                                        </div>
                                    )}
                                </div>
                                <div className="theme-picker-swatches">
                                    {theme.previewColors.map((color, index) => (
                                        <div 
                                            key={index}
                                            className="theme-picker-swatch"
                                            style={{ backgroundColor: color }}
                                        />
                                    ))}
                                </div>
                                <p className="theme-picker-desc">{theme.description}</p>
                            </button>
                        );
                    })}
                </div>
            </CollapsibleSection>

            {/* Collapsible Section: Eternal Journey */}
            <CollapsibleSection title="Our Eternal Journey" icon={Heart} defaultOpen={true}>
                <div className="premium-card" style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '24px', flexWrap: 'wrap' }}>
                        <div className="couple-photo-wrapper groom" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                            <img
                                src={wedding.groomProfilePhoto || 'https://images.unsplash.com/photo-1550009158-9ebf69173e03?auto=format&fit=crop&q=80&w=200'}
                                alt={wedding.groomName}
                                className="couple-profile-img"
                                style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--primary-color)' }}
                            />
                            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>Groom: {wedding.groomName}</span>
                        </div>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                            <span style={{ fontSize: '28px', fontWeight: 800, color: 'var(--primary-color)' }}>{daysRemaining}</span>
                            <span style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)' }}>Days to go</span>
                        </div>

                        <div className="couple-photo-wrapper bride" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                            <img
                                src={wedding.brideProfilePhoto || 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=200'}
                                alt={wedding.brideName}
                                className="couple-profile-img"
                                style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--primary-color)' }}
                            />
                            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>Bride: {wedding.brideName}</span>
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border-subtle)', paddingTop: '16px', flexWrap: 'wrap', gap: '12px' }}>
                        <div>
                            <span style={{ fontSize: '10px', textTransform: 'uppercase', color: 'var(--text-muted)', display: 'block' }}>Wedding Date</span>
                            <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>{weddingDate}</span>
                        </div>
                        <div>
                            <span style={{ fontSize: '10px', textTransform: 'uppercase', color: 'var(--text-muted)', display: 'block' }}>Venue Location</span>
                            <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>{wedding.venue || 'TBA'}, {wedding.city || ''}</span>
                        </div>
                        <div>
                            <span style={{ fontSize: '10px', textTransform: 'uppercase', color: 'var(--text-muted)', display: 'block' }}>Wedding Status</span>
                            <span className={`badge ${
                                wedding.status === 'upcoming' ? 'badge-warning' :
                                wedding.status === 'ongoing' ? 'badge-success' : 'badge-primary'
                            }`}>
                                {wedding.status === 'upcoming' ? 'Upcoming' :
                                 wedding.status === 'ongoing' ? 'Ongoing' : 'Completed'}
                            </span>
                        </div>
                    </div>
                </div>
            </CollapsibleSection>

            {/* Collapsible Section: Plan & Resources */}
            <CollapsibleSection title="Portfolio Resource Limits" icon={BarChart3} defaultOpen={true}>
                <div className="premium-card usage-card" style={{ padding: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h3 className="card-title" style={{ fontSize: '16px', margin: 0 }}>Resource Allocation</h3>
                        <span className={`plan-status-badge ${wedding.plan || 'basic'}`}>
                            {wedding.plan || 'Basic'} Plan
                        </span>
                    </div>

                    <div className="usage-stats" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div className="usage-item">
                            <div className="usage-info" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>
                                <span className="usage-label">Storage Usage</span>
                                <span className="usage-value">
                                    {(wedding.storageUsage ? (wedding.storageUsage / (1024 * 1024)).toFixed(1) : 0)} / {wedding.storageLimit || 0} MB
                                </span>
                            </div>
                            <div className="progress-bar-container" style={{ height: '8px', background: 'rgba(0,0,0,0.05)', borderRadius: '4px', overflow: 'hidden' }}>
                                <div
                                    className="progress-bar"
                                    style={{
                                        height: '100%',
                                        width: `${Math.min(100, ((wedding.storageUsage || 0) / ((wedding.storageLimit || 1) * 1024 * 1024)) * 100)}%`,
                                        background: ((wedding.storageUsage || 0) / ((wedding.storageLimit || 1) * 1024 * 1024)) > 0.9 ? '#ff5252' : 'var(--primary-color)'
                                    }}
                                ></div>
                            </div>
                        </div>

                        <div className="usage-item">
                            <div className="usage-info" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>
                                <span className="usage-label">Guest Invitations</span>
                                <span className="usage-value">
                                    {(guestStats.approvedCount + guestStats.pendingCount)} / {wedding.guestLimit || 100}
                                </span>
                            </div>
                            <div className="progress-bar-container" style={{ height: '8px', background: 'rgba(0,0,0,0.05)', borderRadius: '4px', overflow: 'hidden' }}>
                                <div
                                    className="progress-bar"
                                    style={{
                                        height: '100%',
                                        width: `${Math.min(100, ((guestStats.approvedCount + guestStats.pendingCount) / (wedding.guestLimit || 1)) * 100)}%`,
                                        background: ((guestStats.approvedCount + guestStats.pendingCount) / (wedding.guestLimit || 1)) > 0.9 ? '#ff5252' : 'var(--primary-color)'
                                    }}
                                ></div>
                            </div>
                        </div>
                    </div>

                    {(wedding.plan === 'basic' || !wedding.plan) && (
                        <div className="upgrade-prompt" style={{ marginTop: '16px', fontSize: '12px', color: 'var(--text-muted)' }}>
                            Running low? <span style={{ color: '#d97706', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }} onClick={() => history.push('/pricing')}>Upgrade Plan</span> for more storage and guests.
                        </div>
                    )}
                </div>
            </CollapsibleSection>

            {/* Collapsible Section: Operations Hub */}
            <CollapsibleSection title="Operations Hub" icon={LayoutGrid} defaultOpen={true}>
                <div className="dashboard-grid" style={{ padding: 0 }}>
                    
                    <div className="premium-card stats-card task-stats">
                        <div className="card-header">
                            <span className="card-icon">✅</span>
                            <h3 className="card-title">Tasks</h3>
                        </div>
                        <div className="stats-row">
                            <div className={`stat-pill ${taskStats.pendingCount > 0 ? 'warning' : ''}`}>
                                <span className="stat-value">{taskStats.pendingCount}</span>
                                <span className="stat-label">Pending</span>
                            </div>
                            <div className="stat-pill success">
                                <span className="stat-value">{taskStats.completedCount}</span>
                                <span className="stat-label">Done</span>
                            </div>
                        </div>
                        <button className="primary-btn-premium" onClick={() => history.push('/admin/tasks')}>
                            Manage All Tasks
                        </button>
                    </div>

                    <div className="premium-card stats-card guest-community">
                        <div className="card-header">
                            <span className="card-icon">👥</span>
                            <h3 className="card-title">Guests</h3>
                        </div>
                        <div className="stats-row">
                            <div className="stat-pill primary">
                                <span className="stat-value">{guestStats.approvedCount}</span>
                                <span className="stat-label">Approved</span>
                            </div>
                            <div className={`stat-pill ${guestStats.pendingCount > 0 ? 'warning' : ''}`}>
                                <span className="stat-value">{guestStats.pendingCount}</span>
                                <span className="stat-label">Pending</span>
                            </div>
                        </div>
                        <button className="primary-btn-premium" onClick={() => history.push('/admin/guests')}>
                            Review Requests
                        </button>
                    </div>

                    <div className="premium-card events-card">
                        <div className="card-header">
                            <span className="card-icon">🗓️</span>
                            <h3 className="card-title">Upcoming Events</h3>
                        </div>
                        {ceremonies.length > 0 ? (
                            <div className="events-mini-list">
                                {ceremonies.slice(0, 3).map((c) => (
                                    <div key={c._id} className="event-mini-item">
                                        <div className="event-info">
                                            <p className="event-name">{c.name}</p>
                                            <p className="event-meta">{new Date(c.date).toLocaleDateString()} • {c.time}</p>
                                        </div>
                                        <span className="event-venue-tag">{c.venue}</span>
                                    </div>
                                ))}
                                <button className="outline-btn-premium" onClick={() => history.push('/admin/ceremonies')}>
                                    Full Schedule
                                </button>
                            </div>
                        ) : (
                            <div className="empty-state">
                                <p>No ceremonies scheduled yet.</p>
                                <button className="primary-btn-premium" onClick={() => history.push('/admin/ceremonies')}>
                                    Schedule One
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="premium-card planner-card">
                        <div className="card-header">
                            <span className="card-icon">✨</span>
                            <h3 className="card-title">Mahurat</h3>
                        </div>
                        <p className="card-subtitle" style={{ fontSize: '0.85rem', color: '#666', marginBottom: '16px' }}>
                            Get Shubh Mahurat based on Bride and Groom dates.
                        </p>
                        <button className="primary-btn-premium" onClick={() => history.push('/admin/wedding-planner')}>
                            Find Mahurat
                        </button>
                    </div>

                    <div className="premium-card photographer-card">
                        <div className="card-header">
                            <span className="card-icon">📸</span>
                            <h3 className="card-title">Photographers</h3>
                        </div>
                        <p className="card-subtitle" style={{ fontSize: '0.85rem', color: '#666', marginBottom: '16px' }}>
                            Manage professionals who can upload directly to your gallery.
                        </p>
                        <button className="primary-btn-premium" onClick={() => history.push('/admin/photographers')}>
                            Manage Team
                        </button>
                    </div>

                    <div className="premium-card shadi-wala-card" style={{ background: 'linear-gradient(135deg, #fdfcfb 0%, #e2d1c3 100%)' }}>
                        <div className="card-header">
                            <span className="card-icon">🤖</span>
                            <h3 className="card-title">Shadi Wala</h3>
                        </div>
                        <p className="card-subtitle" style={{ fontSize: '0.85rem', color: '#666', marginBottom: '16px' }}>
                            Our AI Agent provides halls, food, and clothing recommendations.
                        </p>
                        <button className="primary-btn-premium" onClick={() => history.push('/admin/shadi-wala')} style={{ background: '#1e1e2e', color: 'white' }}>
                            Ask AI Agent
                        </button>
                    </div>
                    
                    <div className="premium-card marketplace-card">
                        <div className="card-header">
                            <span className="card-icon">🏪</span>
                            <h3 className="card-title">Marketplace</h3>
                        </div>
                        <p className="card-subtitle" style={{ fontSize: '0.85rem', color: '#666', marginBottom: '16px' }}>
                            Discover and shortlist top vendors for all your wedding needs.
                        </p>
                        <button className="primary-btn-premium" onClick={() => history.push('/marketplace')}>
                            Browse Vendors
                        </button>
                    </div>

                </div>
            </CollapsibleSection>

        </div>
    );
};

export default AdminDashboard;

